<template>
  <b-sidebar
    id="edit-uom-sidebar"
    :visible="!!selectedUOMId"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetUOMData"
    @change="(val) => !val && $emit('updated')"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Edit Unit of Measure</h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->

      <b-overlay :show="isLoading" spinner-variant="primary" style="height: 50%">
        <div class="p-2">
          <u-o-m-form
            :form-data.sync="uomData"
            :loading="isSubmitting || isLoading"
            @submit="onSubmit"
            @cancel="hide"
            v-if="isLoaded"
          />
        </div>
      </b-overlay>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormInvalidFeedback,
  BButton,
  BOverlay,
  BSpinner,
} from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { ref, computed, watch } from '@vue/composition-api';
import { required } from '@validations';
import Ripple from 'vue-ripple-directive';
import store from '@/store';

import UOMForm from './UOMForm.vue';

/**
 {
    "id": 0,
    "name": "string",
    "description": "string",
    "abbreviation": "string",
    "isActive": true,
    "businessID": 4
  }
 */

export default {
  name: 'UOMListEdit',
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BFormInvalidFeedback,
    BButton,
    BOverlay,
    BSpinner,
    UOMForm,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    selectedUOMId: {
      type: [Number],
      default: null,
    },
  },
  setup(props, { emit }) {
    const businessID = computed(() => store.state.user.businessID);
    const isLoading = ref(false);
    const isLoaded = ref(false);
    const isSubmitting = ref(false);

    const blankUOMData = {
      id: 0,
      name: '',
      description: '',
      abbreviation: '',
      isActive: true,
      businessID: businessID.value,
    };

    const uomData = ref(JSON.parse(JSON.stringify(blankUOMData)));

    const resetUOMData = () => {
      uomData.value = JSON.parse(
        JSON.stringify({
          ...blankUOMData,
          businessID: businessID.value,
        })
      );
    };

    const fetchUOMData = async () => {
      if (!props.selectedUOMId) return;

      isLoading.value = true;
      isLoaded.value = false;
      try {
        const response = await store.dispatch('fbr-invoice-unit-of-measure/fetchUOM', {
          id: props.selectedUOMId,
        });

        // Populate form with fetched data (only the specified fields)
        uomData.value = {
          id: response.id,
          name: response.name || '',
          description: response.description || '',
          abbreviation: response.abbreviation || '',
          isActive: response.isActive ?? true,
          businessID: businessID.value,
        };
      } catch (error) {
        console.error('Error fetching UOM:', error);
        // Reset to blank data on error
        resetUOMData();
      } finally {
        isLoading.value = false;
        isLoaded.value = true;
      }
    };

    const onSubmit = async () => {
      // Validate the form first
      const isValid = await refFormObserver.value.validate();
      if (!isValid) return;

      isSubmitting.value = true;

      // Prepare data for submission - include the id
      const submissionData = {
        id: props.selectedUOMId,
        name: uomData.value.name,
        description: uomData.value.description,
        abbreviation: uomData.value.abbreviation,
        isActive: uomData.value.isActive,
        businessID: businessID.value,
      };

      try {
        await store.dispatch('fbr-invoice-unit-of-measure/editUOM', submissionData);

        // Show success toast
        store.dispatch('app/showToast', {
          title: 'Success',
          text: 'Unit of Measure updated successfully',
          variant: 'success',
        });

        emit('refetch-data');
        emit('updated'); // This will hide the form as parent sets selectedUOMId to null
      } catch (error) {
        console.error('Error updating UOM:', error);

        // Show user-friendly error message
        let errorMessage = 'Error updating Unit of Measure. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Show error toast
        store.dispatch('app/showToast', {
          title: 'Error',
          text: errorMessage,
          variant: 'danger',
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    // Watch for selectedUOMId changes to fetch data
    watch(
      () => props.selectedUOMId,
      (newId) => {
        if (newId) {
          fetchUOMData();
        } else {
          resetUOMData();
        }
      },
      { immediate: true }
    );

    return {
      uomData,
      isLoaded,
      isLoading,
      isSubmitting,
      onSubmit,
      resetUOMData,
    };
  },
};
</script>

<style lang="scss">
#edit-uom-sidebar {
  .content-sidebar-header {
    padding: 1rem 1.5rem !important;
  }
}
</style>
